import React from 'react';
import ddt from '@wix/da-ddt';
import { PapiUser } from '@wix/da-papi-types';
import Modal from '../Popup/Modal';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ModalStackItem, ModalType } from './redux/types';
import LoadableMediaManager from '../MediaPopup/MediaManager';
import { MediaSourceType } from '../MediaPopup/types';
import {
  LoadableCommissionEditPopup,
  LoadablePollEditPopup,
  LoadableAddToCollectionPopoverModal,
  LoadablePushNotificationsModal,
  LoadablePostProcurementPopup,
} from './LoadableModals';
import NonContentModals from './NonContentModals';
import LoadableTierPopup from '../Tier/TierPopup';

export type ModalsRenderFunction = (
  modalType: string,
  popModal: () => void,
  params?: any
) => React.ReactNode;

export interface Props {
  children?: any;
  modal?: ModalStackItem;
  currentUser?: PapiUser;
  disableAriaHide?: boolean;
  popModal: () => void;
}

/** Renders a single one of our many modals based on the contents of the `modal` prop */
const DaModal: React.FC<React.PropsWithChildren<Props>> = ({
  modal,
  children,
  popModal,
  currentUser,
  disableAriaHide,
}) => {
  if (!modal) {
    return null;
  }

  const modalType: ModalType = modal.type as ModalType;
  const { options = {} } = modal;
  const { params, wrapInModal, closeBtnClassName, biContext } = options;

  let content;
  if (children) {
    try {
      const renderedChildren = children(modalType, popModal, params);
      content = renderedChildren && (
        <ErrorBoundary debugComponent="DaModal">
          {renderedChildren}
        </ErrorBoundary>
      );
    } catch (e) {
      ddt.error('modals', 'Failed to render custom render function', e);
    }
  }

  if (!content) {
    switch (modalType) {
      case ModalType.CREATE_PREMIUM_FOLDER:
        const reloadPage = event => {
          popModal();
          event && event.preventDefault();
          window.location.reload();
        };
        return (
          <LoadableMediaManager
            initialMediaSource={MediaSourceType.GALLERY}
            withMediaSourceSelector={false}
            withNewGallectionPopupOpen
            onDone={reloadPage}
            onClose={reloadPage}
          />
        );

      case ModalType.COMMISSION:
        return <LoadableCommissionEditPopup onClose={popModal} {...params} />;

      case ModalType.POLL_EDIT:
        return <LoadablePollEditPopup {...params} />;

      case ModalType.TIER_EDIT:
        return <LoadableTierPopup requestClose={popModal} {...params} />;

      case ModalType.COLLECTION_POPOVER:
        return <LoadableAddToCollectionPopoverModal {...params} />;

      case ModalType.PUSH_NOTIFICATIONS:
        return (
          <LoadablePushNotificationsModal requestClose={popModal} {...params} />
        );

      case ModalType.POST_PROCUREMENT:
        return (
          <LoadablePostProcurementPopup onRequestClose={popModal} {...params} />
        );

      default:
        content = (
          <NonContentModals {...{ modalType, popModal, params, currentUser }} />
        );
    }
  }

  if (!content) {
    return;
  }

  if (biContext) {
    content = (
      <BiLoggerContextProvider value={biContext}>
        {content}
      </BiLoggerContextProvider>
    );
  }

  if (wrapInModal) {
    return (
      <Modal
        onRequestClose={popModal}
        withCloseButton
        isOpen
        width="auto"
        height="auto"
        disableAriaHide={disableAriaHide}
        closeBtnClassName={closeBtnClassName}
      >
        {content}
      </Modal>
    );
  }
  return content;
};
DaModal.displayName = 'DaModal';

export default DaModal;
